/**
 * Les clés Query des lectures locales (D21) — **nommées une seule fois, ici**.
 *
 * ⚠️ **Aucune ne porte d'équipe, et c'est le porteur de bases qui le permet** : le cache
 * TanStack vit en mémoire, ne périme jamais (`staleTime: Infinity`) et survivrait à un
 * changement de main, qui **navigue** sans recharger la page. Ce qui le vide est le changement
 * de base lui-même (`offline/team-data.ts`, `onTeamChange`) — une clé à jeter plutôt qu'un
 * segment à ne jamais oublier de poser sur chaque lecture.
 *
 * Elles vivaient à deux endroits, et `['media']` y était même défini **deux fois** : les écrans
 * importaient l'une, l'observateur `liveQuery` l'autre. Deux littéraux identiques s'apparient par
 * coïncidence — le jour où l'un gagne un segment et pas l'autre, l'invalidation de l'observateur
 * cesse d'atteindre la lecture de l'écran, l'écran se fige sans erreur, et rien ne le dit. D'où
 * ce module, dans `offline/` : c'est la couche que **tout le monde** peut importer (les écrans en
 * dépendent déjà, l'inverse est interdit), et ces clés nomment ses tables.
 *
 * Deux familles, et la différence porte sur qui invalide :
 *
 * - `OUTBOX_QUERY_KEY`, `DRAFTS_QUERY_KEY`, `MEDIA_QUERY_KEY`, `PASSAGE_NOTICES_QUERY_KEY`,
 *   `FIELD_APPOINTMENTS_QUERY_KEY` — observées par `watchLocalData`,
 *   qui les invalide **tout seul** à chaque écriture locale, d'où qu'elle vienne ;
 * - `INQUIRIES_QUERY_KEY`, `INTERNAL_MEETINGS_QUERY_KEY`, `VOCABULARY_QUERY_KEY` — sans
 *   observateur : les demandes descendent par la synchro, les médias sont écrits par l'éditeur.
 *   Leur invalidation est **explicite**, à l'endroit qui écrit.
 */

/** La tournée en cache : réécrite par `pullInquiries`. */
export const INQUIRIES_QUERY_KEY = ['inquiries'] as const

/** Les points internes de l'équipe (#319) : remplacés par `pullInternalMeetings`. */
export const INTERNAL_MEETINGS_QUERY_KEY = ['internal-meetings'] as const

/** La saisie en cours, un brouillon par contrôle. */
export const DRAFTS_QUERY_KEY = ['drafts'] as const

/** Les pièces jointes locales, octets compris (D5). */
export const MEDIA_QUERY_KEY = ['media'] as const

/** La file des transmissions (D4). */
export const OUTBOX_QUERY_KEY = ['outbox'] as const

/** La file des avis de passage — observée comme celle des transmissions (#655). */
export const PASSAGE_NOTICES_QUERY_KEY = ['passage-notices'] as const

/** La file des rendez-vous pris sur place — observée comme celle des transmissions (#268). */
export const FIELD_APPOINTMENTS_QUERY_KEY = ['field-appointments'] as const

/**
 * Le référentiel d'anomalies (M17) : réécrit par `pullVocabulary`, dans la base que toutes les
 * équipes partagent (`offline/shared-db.ts`).
 */
export const VOCABULARY_QUERY_KEY = ['vocabulary'] as const

/**
 * Les réglages de l'appareil ({@link DevicePreferences}).
 *
 * ⚠️ **Lus dans la base partagée, comme le référentiel** : c'est la tablette qui est réglée, pas
 * la session (`offline/preferences.ts`). Les ranger par équipe ferait réapparaître le GPS coupé à
 * chaque passation de tablette, ce qui est exactement ce que le réglage sert à éviter. Le vidage
 * du cache au changement d'équipe les fait **relire**, il ne les efface pas : ce qu'il jette est
 * une lecture, et la base partagée, elle, ne bouge pas.
 */
export const PREFERENCES_QUERY_KEY = ['preferences'] as const

/**
 * Les règles du serveur — lues dans la base partagée, comme le référentiel : c'est une règle de
 * GHP, pas une saisie d'équipe.
 */
export const SERVER_RULES_QUERY_KEY = ['server-rules'] as const
